import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FetchStatus, ModalName } from '../../../../enums';
import { useAccount, useEditAccount, useResetEditAccountStatus } from '../../../../features/account';
import { useCloseModal, useModalProps } from '../../../../features/modals';
import { IAccount } from '../../../../interfaces';
import { areValuesValid } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';
import { IEditAccountModalProps } from './types';

export const EditAccountModal = () => {
  const { t } = useTranslation();
  const {
    status: { editAccount: status },
  } = useAccount();
  const editAccount = useEditAccount();
  const resetEditAccountStatus = useResetEditAccountStatus();
  const { account: initialAccount } = useModalProps(ModalName.EDIT_ACCOUNT) as IEditAccountModalProps;
  const [account, setAccount] = useState<IAccount>(initialAccount);
  const closeModal = useCloseModal(ModalName.EDIT_ACCOUNT);

  const handleChange = (key: string) => (value: string) => {
    setAccount({
      ...account,
      [key]: value,
    });
  };

  const isValid = useMemo(() => areValuesValid([account.firstName, account.lastName]), [account]);

  const save = useCallback(() => {
    editAccount(account);
  }, [editAccount, account]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      closeModal();
      resetEditAccountStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button
          text={t('modal.editAccount.button.save')}
          disabled={!isValid}
          loading={status === FetchStatus.LOADING}
          onClick={save}
        />
        <Button text={t('modal.editAccount.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, save, isValid, status]);

  return (
    <Modal name={ModalName.EDIT_ACCOUNT} header={t('modal.editAccount.title')} footer={footer}>
      <p className="typography-paragraph-3 text-text-secondary mb-6">{t('modal.editAccount.description')}</p>

      <FormFields>
        <Input
          value={account.firstName}
          label={t('modal.editAccount.field.firstName.label')}
          onChange={handleChange('firstName')}
          placeholder={t('modal.editAccount.field.firstName.placeholder')}
        />

        <Input
          value={account.lastName}
          label={t('modal.editAccount.field.lastName.label')}
          onChange={handleChange('lastName')}
          placeholder={t('modal.editAccount.field.lastName.placeholder')}
        />

        <Input
          value={account.telephone}
          label={t('modal.editAccount.field.telephone.label')}
          onChange={handleChange('telephone')}
          placeholder={t('modal.editAccount.field.telephone.placeholder')}
        />
      </FormFields>
    </Modal>
  );
};
